import { Button, Card } from "@validereinc/common-components";
import { Investigations } from "@validereinc/domain-controllers";
import {
  ApprovalDecision,
  type ApprovalDecisionType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React from "react";
import styles from "./PendingApprovalCard.module.scss";

const {
  getApprovalDecisionDisplayLabel,
  PendingApprovalCardDefaultApproveLabel,
  PendingApprovalCardDefaultHeading,
  PendingApprovalCardDefaultRejectLabel,
  PendingApprovalCardUnknownPayloadMessage,
  InvestigationWriteApprovalDecisionUnavailableMessage,
  UnknownApprovalDecisionDisplayKey,
} = Investigations.view;

type DisplayedApprovalDecisionType = Parameters<
  typeof getApprovalDecisionDisplayLabel
>[0];

const cx = classNames.bind(styles);

export type PendingApprovalCardDecisionType =
  | typeof ApprovalDecision.APPROVE
  | typeof ApprovalDecision.REJECT;

export type PendingApprovalCardProps = {
  children?: React.ReactNode;
  isLoading: boolean;
  isReadOnly?: boolean;
  isResolved?: boolean;
  heading?: string;
  decision?: ApprovalDecisionType;
  fallbackMessage?: string;
  approveLabel?: string;
  rejectLabel?: string;
  isDecisionPermitted?: boolean;
  isDecisionPermissionLoading?: boolean;
  decisionUnavailableMessage?: string;
  onDecide: (decision: PendingApprovalCardDecisionType) => void;
};

export const PendingApprovalCard = ({
  children,
  isLoading,
  isReadOnly = false,
  isResolved = false,
  heading = PendingApprovalCardDefaultHeading,
  decision,
  fallbackMessage = PendingApprovalCardUnknownPayloadMessage,
  approveLabel = PendingApprovalCardDefaultApproveLabel,
  rejectLabel = PendingApprovalCardDefaultRejectLabel,
  isDecisionPermitted = false,
  isDecisionPermissionLoading = false,
  decisionUnavailableMessage = InvestigationWriteApprovalDecisionUnavailableMessage,
  onDecide,
}: PendingApprovalCardProps) => {
  const showDecisionUnavailableMessage =
    !isReadOnly &&
    !isResolved &&
    !decision &&
    !isDecisionPermitted &&
    !isDecisionPermissionLoading;
  const showDecisionActions =
    !isReadOnly &&
    !isResolved &&
    !decision &&
    (isDecisionPermitted || isDecisionPermissionLoading);
  const areDecisionActionsDisabled = isLoading || isDecisionPermissionLoading;

  const displayedDecision: DisplayedApprovalDecisionType | null =
    decision ?? (isResolved ? UnknownApprovalDecisionDisplayKey : null);
  const displayedHeading = heading.trim() || PendingApprovalCardDefaultHeading;

  return (
    <Card
      className={cx("pending-approval-card", {
        "pending-approval-card--readonly": isReadOnly,
        [`pending-approval-card--decision-${displayedDecision}`]:
          Boolean(displayedDecision),
      })}
      data-testid="pending-approval-card"
      data-readonly={isReadOnly ? "true" : undefined}
    >
      <div className={cx("pending-approval-card__details")}>
        <div className={cx("pending-approval-card__header")}>
          <div className={cx("pending-approval-card__header-title")}>
            <div className={cx("pending-approval-card__heading")}>
              {displayedHeading}
            </div>
          </div>
          {displayedDecision ? (
            <div
              className={cx(
                "pending-approval-card__header-status",
                `pending-approval-card__header-status--${displayedDecision}`
              )}
            >
              {getApprovalDecisionDisplayLabel(displayedDecision)}
            </div>
          ) : null}
        </div>

        {children ?? (
          <p className={cx("pending-approval-card__fallback")}>
            {fallbackMessage}
          </p>
        )}

        {showDecisionUnavailableMessage ? (
          <p className={cx("pending-approval-card__fallback")}>
            {decisionUnavailableMessage}
          </p>
        ) : null}

        {showDecisionActions ? (
          <div className={cx("pending-approval-card__actions")}>
            <Button
              variant="error-outline"
              size="small"
              disabled={areDecisionActionsDisabled}
              onClick={() => {
                onDecide(ApprovalDecision.REJECT);
              }}
            >
              {rejectLabel}
            </Button>
            <Button
              variant="primary"
              size="small"
              disabled={areDecisionActionsDisabled}
              onClick={() => {
                onDecide(ApprovalDecision.APPROVE);
              }}
            >
              {approveLabel}
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
};
